import * as React from 'react';
import { useCallback, useEffect } from 'react';

import appInfo from 'js/lib/appInfo';
import { getAuthMode, getLocationHash, stripAuthModeFromHash } from 'js/lib/authModeDeepLinkUtils';
import store from 'js/lib/coursera.store';
import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import AuthenticationModal from 'bundles/authentication-common/components/authenticationModal/AuthenticationModal';
import {
  type AUTHENTICATION_MODAL_MODES_KEY,
  isAuthenticationModalMode,
} from 'bundles/authentication-common/constants';
import useBotManager from 'bundles/authentication-common/hooks/useBotManager';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { AUTH_PROVIDER_LOCAL_STORAGE_KEY } from 'bundles/page/common/constants';
import { ARKOSE_BYPASS_APP } from 'bundles/user-account/common/constants';
import type { AuthenticationContentBaseProps, AuthenticationModalBaseProps } from 'bundles/user-account/types';
import { sanitizeAuthProvider } from 'bundles/user-account/utils/AuthenticationOptionUtils';

type Props = AuthenticationContentBaseProps & AuthenticationModalBaseProps;

/**
 * AuthenticationOption is a component that handles authentication modal display logic.
 * It manages the auth mode (login/signup) based on URL parameters and app context,
 * and handles Arkose bot protection integration.
 *
 * The component will:
 * - Show authentication modal when needed
 * - Remove invalid auth modes from URL
 * - Handle Arkose bot protection for specific apps
 * - Bypass Arkose for certain pages to prevent initialization conflicts
 *
 * To be used wherever authentication is needed.
 * Note: PageHeader loads the component, so if your page has the header, there is not need to explicitly call this in your page.
 * When you need to gate logged-in content, simply redirect logged-out user to the page with `authMode` query param.
 * The auth modal will appear automatically.
 * Example: https://www.coursera.org/account-profile?authMode=login
 */
const AuthenticationOption = (props: Props) => {
  const {
    isEnterprise,
    programId,
    preventModalClose,
    preventRegister,
    onCloseRedirectURL,
    utilizeWebRedirectOnClose,
    defaultMode,
  } = props;

  const location = useLocation();
  const { query } = location;
  const hash = getLocationHash(location);

  // We need to bypass Arkose initialization on degree and teams pages
  // that include another authentication form with a separate Arkose initialization.
  // Without this bypass, Arkose would be initialized twice, leading to potential race conditions
  // and conflicts with `window.enforcement`.
  // [Design doc](https://docs.google.com/document/d/11rrksObuKCI5hCI5jQeuyDuB3W7vGMK-_0zKd9Xsau0/edit?tab=t.0#bookmark=id.a1s4eggqi9gb)
  const appName = appInfo.get().appName || '';
  const shouldBypassArkose = ARKOSE_BYPASS_APP.includes(appName) || isAuthenticatedUser();

  const { arkoseBotManager, isRetrievingTokenRef, canSwitchArkoseKey } = useBotManager({
    shouldBypassRecaptcha: true,
    shouldBypassArkose,
  });

  const context = {
    arkoseBotManagerContext: shouldBypassArkose
      ? undefined
      : {
          arkoseBotManager,
          isRetrievingTokenRef,
          canSwitchArkoseKey,
        },
  };

  useEffect(() => {
    // check if authProvider exists, if yes, write the sanitized string to localStorage
    if (query?.authProvider) {
      const sanitizedAuthProviderString = sanitizeAuthProvider(query.authProvider);
      if (sanitizedAuthProviderString) {
        // @ts-expect-error TSMIGRATION
        store.setUnserialized(AUTH_PROVIDER_LOCAL_STORAGE_KEY, sanitizedAuthProviderString);
      }
    }
  }, [query?.authProvider]);

  const updateRouterParams = useCallback<() => void>(() => {
    const { pathname, search } = location;
    const searchParams = new URLSearchParams(search);
    const queryHadAuthMode = searchParams.has('authMode');
    let newHash: string;
    if (queryHadAuthMode) {
      searchParams.delete('authMode');
      // Preserve the full hash when cleaning an invalid query authMode so a valid `#authMode=…`
      // can still be applied on the next render (query wins over hash until query is fixed).
      newHash = hash || '';
    } else {
      newHash = stripAuthModeFromHash(hash);
    }
    const newSearch = searchParams.toString();
    const newPath = `${pathname}${newSearch ? `?${newSearch}` : ''}${newHash || ''}`;

    window.history.replaceState(null, '', newPath);
  }, [location, hash]);

  const getValidAuthMode = useCallback<() => AUTHENTICATION_MODAL_MODES_KEY | undefined>(() => {
    if (isAuthenticatedUser()) {
      // no need for auth
      return undefined;
    }

    const authMode = getAuthMode(query, hash) || defaultMode;
    if (!authMode) {
      return undefined;
    } else if (isAuthenticationModalMode(authMode)) {
      return authMode;
    } else {
      if (typeof window !== 'undefined') {
        updateRouterParams();
      }
      return undefined;
    }
  }, [query, hash, updateRouterParams, defaultMode]);

  const authMode = getValidAuthMode();

  if (!authMode) {
    return null;
  } else {
    return (
      <AuthenticationModal
        onCloseRedirectURL={onCloseRedirectURL}
        utilizeWebRedirectOnClose={utilizeWebRedirectOnClose}
        preventModalClose={preventModalClose}
        preventRegister={preventRegister}
        isEnterprise={isEnterprise}
        defaultMode={authMode}
        programId={programId}
        context={context}
      />
    );
  }
};

const AuthenticationOptionWithErrorBoundary = (props: Props) => (
  <ErrorBoundaryWithTagging componentName="user-account/AuthenticationOption">
    <AuthenticationOption {...props} />
  </ErrorBoundaryWithTagging>
);

export default AuthenticationOptionWithErrorBoundary;
